import { ConvexReactClient } from "convex/react";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { useAuth } from "./hooks/useAuth";
import LoginScreen from "./components/LoginScreen";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import AdminOverview from "./components/AdminOverview";
import ArtistOverview from "./components/ArtistOverview";
import Projects from "./components/Projects";
import AdminProjects from "./components/AdminProjects";
import AdminPacks from "./components/AdminPacks";
import AdminPaymentsEmails from "./components/AdminPaymentsEmails";
import ArtistProfileLinker from "./components/ArtistProfileLinker";
import ArtistProfileModal from "./components/ArtistProfileModal";
import ProjectReview from "./components/ProjectReview";
import Daw from "./components/Daw";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { FALLBACK_ARTISTS, TABS, type TabId } from "./data";

// Keep production resilient when a clean Pages build does not receive the
// ignored local .env.local file. The Convex deployment URL is public; the
// authenticated session still controls all portal data access.
const productionConvexUrl = "https://dashing-bee-919.convex.cloud";
const convexUrl = import.meta.env.VITE_CONVEX_URL || (import.meta.env.PROD ? productionConvexUrl : "");
const convex = new ConvexReactClient(convexUrl || productionConvexUrl);

export default function App() {
  return (
    <ConvexAuthProvider client={convex}>
      <PortalRouter />
    </ConvexAuthProvider>
  );
}

function Portal() {
  const { email, isAdmin, isAuthenticated, isLoading, logout, temporaryPassword } =
    useAuth();
  const portalData = useQuery(api.queries.getPortalData, isAuthenticated ? {} : "skip");
  const availableArtistProfiles = useQuery(
    api.queries.getAvailableArtistProfiles,
    isAuthenticated && !isAdmin ? {} : "skip",
  );

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [isAdminWorkspace, setIsAdminWorkspace] = useState(isAdmin);
  const [hasSwitched, setHasSwitched] = useState(false);
  const [selectedArtistId, setSelectedArtistId] = useState<string>(FALLBACK_ARTISTS[0].id);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [editingArtistId, setEditingArtistId] = useState<string | null>(null);

  const artists = useMemo(
    () => portalData?.artists ?? FALLBACK_ARTISTS,
    [portalData],
  );
  const showAdmin = isAdmin && isAdminWorkspace;

  // Keep selectedArtistId valid
  useEffect(() => {
    if (artists.length !== 0 && !artists.some((a) => a.id === selectedArtistId)) {
      setSelectedArtistId(artists[0].id);
    }
  }, [artists, selectedArtistId]);

  // Auto-switch to admin workspace on first load
  useEffect(() => {
    if (!isAuthenticated) {
      setIsAdminWorkspace(false);
      setHasSwitched(false);
      return;
    }
    if (!isAdmin) {
      setIsAdminWorkspace(false);
      return;
    }
    if (!hasSwitched) setIsAdminWorkspace(true);
  }, [isAdmin, isAuthenticated, hasSwitched]);

  // If leaving admin workspace, reset to overview tab
  useEffect(() => {
    if (!showAdmin && TABS.find((t) => t.id === activeTab)?.adminOnly) {
      setActiveTab("overview");
    }
  }, [activeTab, showAdmin]);

  // Clear deep-linked projects when leaving both project-detail surfaces.
  useEffect(() => {
    if (activeTab !== "files") {
      setOpenProjectId(null);
    }
  }, [activeTab, showAdmin]);

  const selectedArtist = artists.find((a) => a.id === selectedArtistId) ?? artists[0];
  const editingArtist = artists.find((artist) => artist.id === editingArtistId);

  const totalProjects = artists.reduce((sum, a) => sum + a.projects.length, 0);
  const activeArtists = artists.filter((a) => a.status === "active").length;

  function switchToArtistWorkspace(artistId: string) {
    setSelectedArtistId(artistId);
    setHasSwitched(true);
    setIsAdminWorkspace(false);
  }

  function switchToAdminWorkspace() {
    setHasSwitched(true);
    setIsAdminWorkspace(true);
    setActiveTab("overview");
  }

  if (isLoading) {
    return <PortalLoading />;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (temporaryPassword) {
    return (
      <div className="app-root">
        <div className="fixed inset-0 music-pattern-themed" aria-hidden="true" />
        <main className="password-gate">
          <PasswordGate description="This account is using a temporary password. Create your own password before opening the portal." forced onCancel={logout} />
        </main>
      </div>
    );
  }

  if (
    !isAdmin &&
    portalData !== undefined &&
    (portalData?.artists.length ?? 0) === 0
  ) {
    return (
      <ArtistProfileLinker
        email={email}
        logout={logout}
        profiles={availableArtistProfiles}
      />
    );
  }

  return (
    <div className="app-root">
      <div className="fixed inset-0 music-pattern-themed" aria-hidden="true" />
      <Navbar
        artists={artists}
        email={email}
        isAdmin={isAdmin}
        isAdminWorkspace={showAdmin}
        logout={logout}
        selectedArtist={selectedArtist}
        onEditArtistProfile={(artistId) => setEditingArtistId(artistId)}
        switchToAdminWorkspace={switchToAdminWorkspace}
        switchToArtistWorkspace={switchToArtistWorkspace}
      />
      <div className="app-layout">
        <Sidebar activeTab={activeTab} isAdminWorkspace={showAdmin} setActiveTab={setActiveTab} />
        <main className="app-main">
          <div className="animate-fade-in">
            {activeTab === "overview" ? (
              showAdmin ? (
                <AdminOverview
                  activeArtists={activeArtists}
                  artists={artists}
                  selectedArtist={selectedArtist}
                  setActiveTab={setActiveTab}
                  totalArtists={artists.length}
                  totalProjects={totalProjects}
                  onEditArtistProfile={() => setEditingArtistId(selectedArtist.id)}
                  onOpenProject={(artistId, projectId) => {
                    setSelectedArtistId(artistId);
                    setOpenProjectId(projectId);
                    setActiveTab("files");
                  }}
                />
              ) : (
                <ArtistOverview
                  onOpenProject={(id) => {
                    setOpenProjectId(id);
                    setActiveTab("files");
                  }}
                  selectedArtist={selectedArtist}
                  setActiveTab={setActiveTab}
                />
              )
            ) : null}
            {activeTab === "files" ? (
              showAdmin ? (
                openProjectId ? (
                  <Projects
                    isAdmin
                    initialProjectId={openProjectId}
                    onCloseProject={() => setOpenProjectId(null)}
                    onOpenProjectConsumed={() => undefined}
                    selectedArtist={selectedArtist}
                  />
                ) : (
                  <AdminProjects />
                )
              ) : (
                <Projects
                  isAdmin={showAdmin}
                  initialProjectId={openProjectId}
                  onOpenProjectConsumed={() => setOpenProjectId(null)}
                  selectedArtist={selectedArtist}
                />
              )
            ) : null}
            {activeTab === "daw" && showAdmin ? <Daw /> : null}
            {activeTab === "packs" && showAdmin ? <AdminPacks /> : null}
            {activeTab === "payments" && showAdmin ? <AdminPaymentsEmails /> : null}
            {activeTab === "settings" ? (
              showAdmin ? (
                <AdminSettings selectedArtist={selectedArtist} />
              ) : (
                <ArtistSettings email={email} selectedArtist={selectedArtist} />
              )
            ) : null}
          </div>
        </main>
      </div>
      {editingArtist ? (
        <ArtistProfileModal
          artist={editingArtist}
          onClose={() => setEditingArtistId(null)}
        />
      ) : null}
    </div>
  );
}

function PortalLoading() {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), 10_000);
    return () => window.clearTimeout(timer);
  }, []);

  if (timedOut) {
    return (
      <main className="portal-error" role="alert">
        <section className="portal-error-card">
          <p className="portal-error-label">Johnsonboibeats</p>
          <h1>Portal connection timed out</h1>
          <p>The portal could not connect to its authentication service. Reload to try again.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload portal
          </button>
        </section>
      </main>
    );
  }

  return (
    <div className="app-loading" role="status" aria-live="polite">
      <div className="spinner" />
      <span>Loading portal…</span>
    </div>
  );
}

function guestReviewToken(): string | null {
  const match = window.location.pathname.match(/^\/review\/([^/]+)\/?$/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function PortalRouter() {
  const token = guestReviewToken();
  return token ? <ProjectReview mode="guest" token={token} /> : <Portal />;
}

// ── Lazy-load password gate to avoid circular deps ────────────────
import PasswordGate from "./components/PasswordGate";
import AdminSettings from "./components/AdminSettings";
import ArtistSettings from "./components/Settings";
